import { useEffect, useMemo, useState } from "react";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { useActivityFeed } from "../context/ActivityFeedContext";

/**
 * useRecentRR
 * Builds RR series from activities within a time window that match the same variant/repo.
 *
 * @param {object} params
 * @param {string} params.uid
 * @param {string} params.tag - Firestore tag (e.g. "breatheultra")
 * @param {string} [params.variant] - e.g. "RecordingForcedSpirometry"
 * @param {string} [params.repo]     - e.g. "pdx-recording-forced-spirometry"
 * @param {number} [params.windowDays=30]
 * @param {number} [params.limit=12]
 * @param {boolean} [params.debug=false]
 */
export function useRecentRR({
  uid,
  tag = "breatheultra",
  variant,
  repo,
  windowDays = 30,
  limit = undefined,
  debug = false,
  rrField, // optional string key for RR in the measurement doc
  rrExtractor,
  anchorDate, // NEW: Date or ms to anchor the window (selected activity date)
  includeCurrent = true,
}) {
  const { items } = useActivityFeed();
  const [state, setState] = useState({ loading: true, data: [], error: null });

  const log = (...args) => debug && console.log("[useRecentRR]", ...args);
  const group = (label) =>
    debug && console.groupCollapsed(`[useRecentRR] ${label}`);
  const groupEnd = () => debug && console.groupEnd();

  // Window is [startOfDay(anchor - (windowDays-1)), endOfDay(anchor)]
  const { windowStartMs, windowEndMs } = useMemo(() => {
    const tz = "Europe/London";
    const anchor =
      anchorDate instanceof Date
        ? anchorDate
        : new Date(anchorDate ?? Date.now());
    const end = new Date(anchor);
    // endOfDay (local)
    end.setHours(23, 59, 59, 999);
    const start = new Date(anchor);
    start.setDate(start.getDate() - (windowDays - 1));
    start.setHours(0, 0, 0, 0);
    return { windowStartMs: start.getTime(), windowEndMs: end.getTime() };
  }, [anchorDate, windowDays]);

  const candidates = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const before = list.length;

    const norm = (s) => String(s ?? "").toLowerCase();
    const matchGroup = (value, ...fields) => {
      const v = norm(value);
      return fields.some((f) => {
        const x = norm(f);
        return x === v || x.includes(v) || v.includes(x);
      });
    };

    const matchesType = (it) => {
      const v = norm(it?.variant);
      const r = norm(it?.repo);
      if (variant && repo)
        return matchGroup(variant, v, r) && matchGroup(repo, r, v);
      if (variant) return matchGroup(variant, v, r);
      if (repo) return matchGroup(repo, r, v);
      return true;
    };

    const filtered = list
      .filter(
        (it) =>
          it?.timestamp &&
          it.timestamp >= windowStartMs &&
          it.timestamp <= windowEndMs &&
          matchesType(it)
      )
      .sort((a, b) => b.timestamp - a.timestamp);
    if (typeof limit === "number" && limit > 0) {
      filtered = filtered.slice(0, limit);
    }

    if (debug) {
      group("candidates");
      log("params:", {
        uid,
        tag,
        variant,
        repo,
        windowDays,
        limit,
        anchorDate,
        includeCurrent,
      });
      log("items count:", before);
      log("window:", {
        windowStartMs,
        windowEndMs,
        startISO: new Date(windowStartMs).toISOString(),
        endISO: new Date(windowEndMs).toISOString(),
      });
      log("list pre filter:", list);

      log("filtered (post match + window) count:", filtered.length);
      log(
        "candidates (post-sort/slice):",
        filtered.map((c) => ({
          baseName: c.baseName,
          ts: c.timestamp,
          date: new Date(c.timestamp).toISOString(),
          variant: c.variant,
          repo: c.repo,
        }))
      );
      groupEnd();
    }
    return filtered;
  }, [
    items,
    windowStartMs,
    windowEndMs,
    limit,
    variant,
    repo,
    uid,
    tag,
    windowDays,
    anchorDate,
    includeCurrent,
    debug,
  ]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const t0 = performance.now();
      group("run()");
      log("start with params:", {
        uid,
        tag,
        variant,
        repo,
        windowDays,
        limit,
        anchorDate,
        includeCurrent,
      });
      log("param types:", {
        uid_type: typeof uid,
        tag_type: typeof tag,
      });
      log("candidates length:", candidates.length);

      if (!uid || !tag) {
        log("missing uid or tag → abort");
        setState({ loading: false, data: [], error: null });
        groupEnd();
        return;
      }

      try {
        const db = getFirestore();
        const out = [];

        if (candidates.length === 0) {
          log("no candidates in window; returning empty data");
        }

        await Promise.all(
          candidates.map(async (it, idx) => {
            // Harden path segments
            const segUid = typeof uid === "string" ? uid : String(uid ?? "");
            const segTag = typeof tag === "string" ? tag : String(tag ?? "");
            const segBase =
              typeof it?.baseName === "string"
                ? it.baseName
                : String(it?.baseName ?? "");

            if (!segUid || !segTag || !segBase) {
              log(`#${idx} invalid path segments`, {
                segUid,
                segTag,
                segBase,
                raw: { uid, tag, baseName: it?.baseName },
              });
              return;
            }

            const ref = doc(db, "measurements", segUid, segTag, segBase);
            const tDoc = performance.now();
            try {
              const snap = await getDoc(ref);
              log(
                `#${idx} getDoc(${it.baseName}) ms=`,
                Math.round(performance.now() - tDoc)
              );

              if (!snap.exists()) {
                log(`#${idx} doc missing`, { baseName: it.baseName });
                return;
              }

              const d = snap.data() || {};
              log(d);
              const rrRaw = d?.rr_median_ipi;
              log(rrRaw);
              // 1) custom extractor takes precedence
              let rr;
              if (typeof rrExtractor === "function") {
                const v = rrExtractor(d);
                rr = Number(v);
              } else if (rrField) {
                // 2) explicit field name
                rr = Number(d?.[rrField]);
              } else {
                // 3) safe defaults by schema: spirometry OR exertion
                const v =
                  d?.rr_median_ipi ?? d?.estimated_rr_bpm ?? d?.rr ?? undefined;
                rr = Number(v);
              }
              log("resolved rr =", rr);
              if (!Number.isFinite(rr)) {
                log(`#${idx} rr not finite`, { baseName: it.baseName, rrRaw });
                return;
              }

              // Prefer measurement timestamp if present; else activity timestamp.
              // Guard against seconds-vs-ms: if ts looks like seconds, convert to ms.
              let ts;
              if (d?.ts && typeof d.ts.toMillis === "function") {
                ts = d.ts.toMillis();
              } else if (Number.isFinite(d?.ts)) {
                ts = Number(d.ts);
              } else {
                ts = Number(it.timestamp) || 0;
              }
              if (ts > 0 && ts < 2e10) {
                // if ts < ~Jul 2033 in ms, and very likely seconds (<= 2e10),
                // treat values below 1e12 as seconds and convert
                if (ts < 1e12) ts = ts * 1000;
              }
              if (!ts) {
                log(`#${idx} timestamp missing/0, skipping`, {
                  baseName: it.baseName,
                });
                return;
              }

              const dd = new Date(ts);
              const label = dd.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
              });

              // Keep raw rr (no rounding here) for accurate min/max/avg later
              const point = { ts, rr };
              log(`#${idx} OK`, point);
              out.push(point);
            } catch (e) {
              log(`#${idx} getDoc error`, { baseName: it.baseName, error: e });
            }
          })
        );

        out.sort((a, b) => a.ts - b.ts); // oldest → newest

        if (!cancelled) {
          setState({ loading: false, data: out, error: null });
        }

        log("final points:", out);
        log("total elapsed ms:", Math.round(performance.now() - t0));
        groupEnd();
      } catch (e) {
        log("fatal error:", e);
        if (!cancelled) setState({ loading: false, data: [], error: e });
        groupEnd();
      }
    }

    run();
    return () => {
      cancelled = true;
      log("cancelled effect");
    };
  }, [uid, tag, candidates, debug, variant, repo]);

  return state; // { loading, data:[{ts,label,rr}], error }
}
